import React, {FunctionComponent} from "react";
import {makeStyles} from "tss-react/mui";
import {
    AppBar,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    Theme,
    Toolbar,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {CLOSE_STATION_INFO} from "@/Redux/Disposition/Constants";
import {MapContainer, Marker, Popup, TileLayer} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, {LatLngExpression} from "leaflet";

import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import {useAppDispatch, useAppSelector} from "@/Redux/Redux";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
});

L.Marker.prototype.options.icon = DefaultIcon;

interface StationModalProps {
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },

    submit: {},

    buttonProgress: {
        position: "absolute",
        top: "54%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },

    wrapper: {
        margin: theme.spacing(1),
        marginTop: theme.spacing(2),
        position: "relative",
    },

    appBar: {
        position: "relative",
        boxShadow: "none",
    },

    title: {
        flex: 1,
    },
}));

const StationInfoModal: FunctionComponent<
    StationModalProps
> = ({}): JSX.Element => {
    const {classes} = useStyles();
    const {stationInfoOverlayItem} = useAppSelector(
        (state) => state.disposition,
    );
    const {stations} = useAppSelector((state) => state.app);
    const dispatch = useAppDispatch();
    const {t} = useTranslation();

    let station = null;
    for (const index in stations.data) {
        if (
            stations.data[index].stationId === stationInfoOverlayItem.stationId
        ) {
            station = stations.data[index];
            break;
        }
    }
    const position: LatLngExpression = [
        station?.latitude || 0,
        station?.longitude || 0,
    ];

    const handleModalClose = () => {
        dispatch({type: CLOSE_STATION_INFO});
    };

    return (
        <Dialog
            onClose={handleModalClose}
            aria-labelledby="customized-dialxog-title"
            open={true}
            fullWidth={true}
            maxWidth={"md"}
        >
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <Typography variant="h6" className={classes.title}>
                        Stationsnummer: {station?.stationId}
                    </Typography>
                    <IconButton aria-label="close" onClick={handleModalClose} data-testid={"closeStationInfo"}>
                        <CloseIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <DialogActions
                style={{justifyContent: "flex-start"}}
            ></DialogActions>
            <DialogContent>
                <Grid container spacing={0}>
                    <Grid item xs={6}>
                        <Grid
                            container
                            spacing={0}
                            style={{marginBottom: 10}}
                        >
                            <Grid item xs={4}>
                                {t("disposition.fields.station")}
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.stationId}</b>
                            </Grid>
                            <Grid item xs={4}>
                                {t("disposition.fields.name")}
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.disponame}</b>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            spacing={0}
                            style={{marginBottom: 10}}
                        >
                            <Grid item xs={4}>
                                {t("disposition.fields.street")}
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.street}</b>
                            </Grid>
                            <Grid item xs={4}>
                                {t("disposition.fields.zipCode")}
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.zipcode}</b>
                            </Grid>
                            <Grid item xs={4}>

                                {t("disposition.fields.location")}
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.city}</b>
                            </Grid>
                            <Grid item xs={4}>
                                {t("disposition.fields.phone")}:
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.phonenumber}</b>
                            </Grid>
                            <Grid item xs={4}>
                                {t("disposition.fields.email")}:
                            </Grid>
                            <Grid item xs={8}>
                                <b>{station?.email}</b>
                            </Grid>
                            {station?.opentime?.map(
                                (openday: any, index: number) => {
                                    const starttime =
                                        openday.starttime.slice(0, 2) +
                                        ":" +
                                        openday.starttime.slice(2);
                                    const endtime =
                                        openday.endtime.slice(0, 2) +
                                        ":" +
                                        openday.endtime.slice(2);
                                    const breakStarttime =
                                        openday.breakStarttime.slice(0, 2) +
                                        ":" +
                                        openday.breakStarttime.slice(2);
                                    const breakEndtime =
                                        openday.breakEndtime.slice(0, 2) +
                                        ":" +
                                        openday.breakEndtime.slice(2);

                                    return (
                                        // React.Fragment is the same as <></> but it allows for a key prop to be provided
                                        <React.Fragment key={index}>
                                            <Grid item xs={4}>
                                                {index == 0
                                                    ? t(
                                                    "disposition.fields.openTime",
                                                ) + ":"
                                                    : ""}{" "}
                                            </Grid>
                                            <Grid item xs={2}>
                                                <b>{openday?.weekDay}</b>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <b>
                                                    {breakStarttime > starttime
                                                        ? starttime +
                                                        " - " +
                                                        breakStarttime +
                                                        " " +
                                                        breakEndtime +
                                                        " - " +
                                                        endtime
                                                        : starttime +
                                                        " - " +
                                                        endtime}
                                                </b>
                                            </Grid>
                                        </React.Fragment>
                                    );
                                },
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={6}>
                        <MapContainer
                            center={position}
                            zoom={100}
                            style={{height: 400}}
                            scrollWheelZoom={true}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={position}>
                                <Popup>
                                    A pretty CSS3 popup. <br/> Easily
                                    customizable.
                                </Popup>
                            </Marker>
                        </MapContainer>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default StationInfoModal;
